import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '../ui/command';
import ConfirmDialog from '../ConfirmDialog';
import { updateOrganisation } from '../../services/organisation.service';
import { clearOrganisationCache } from '../../hooks/useOrganisation';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import {
    TIMEZONES,
    POPULAR_TIMEZONE_NAMES,
    formatTimezoneLabel,
    getTimezoneOffsetLabel,
} from '../../data/timezones';
import { cn } from '../../utils';

const getSearchKeywords = (timezone) => {
    const segments = timezone.split('/');
    const city = segments[segments.length - 1]?.replace(/_/g, ' ');
    return [
        timezone,
        formatTimezoneLabel(timezone),
        city,
        ...segments,
        getTimezoneOffsetLabel(timezone),
    ].filter(Boolean);
};

const TimezoneOption = ({ timezone, selected, onSelect }) => (
    <CommandItem
        key={timezone}
        value={timezone}
        keywords={getSearchKeywords(timezone)}
        onSelect={() => onSelect(timezone)}
    >
        <Check className={cn('mr-2 h-4 w-4', selected === timezone ? 'opacity-100' : 'opacity-0')} />
        <span className="font-medium">{formatTimezoneLabel(timezone)}</span>
        <span className="ml-2 text-slate-500">{getTimezoneOffsetLabel(timezone)}</span>
    </CommandItem>
);

export default function TimezoneCard({ timezone, onUpdated }) {
    const { user } = useAuth();
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(timezone || '');

    useEffect(() => {
        setSelected(timezone || '');
    }, [timezone]);

    const savedTimezone = timezone || '';
    const isDirty = selected !== savedTimezone;
    const canSave = Boolean(selected) && isDirty;

    const popularTimezones = useMemo(
        () => POPULAR_TIMEZONE_NAMES.filter((tz) => TIMEZONES.includes(tz)),
        []
    );

    const otherTimezones = useMemo(
        () => TIMEZONES.filter((tz) => !POPULAR_TIMEZONE_NAMES.includes(tz)),
        []
    );

    const handleSelect = (tz) => {
        setSelected(tz);
        setOpen(false);
    };

    const handleConfirm = async () => {
        const response = await updateOrganisation(
            user.organisationId,
            { timezone: selected },
            user.token
        );

        if (response && response.status === 200) {
            clearOrganisationCache();
            toast({
                title: 'Success',
                description: 'Timezone updated',
                variant: 'success',
            });
            onUpdated && onUpdated();
        } else {
            const message = response?.data?.error?.message || 'Error updating timezone';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                        <Clock className="h-4 w-4 text-slate-500" />
                    </span>
                    <h3 className="my-0">Timezone</h3>
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                    Set the organisation timezone used when displaying and scheduling dates and times.
                </p>

                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full justify-between font-normal"
                        >
                            {selected ? (
                                <span className="truncate">
                                    {formatTimezoneLabel(selected)}
                                    <span className="ml-2 text-slate-400">{getTimezoneOffsetLabel(selected)}</span>
                                </span>
                            ) : (
                                'Select timezone...'
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                            <CommandInput placeholder="Search timezones..." />
                            <CommandList>
                                <CommandEmpty>No timezones found.</CommandEmpty>
                                <CommandGroup heading="Popular">
                                    {popularTimezones.map((tz) => (
                                        <TimezoneOption
                                            key={tz}
                                            timezone={tz}
                                            selected={selected}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </CommandGroup>
                                <CommandSeparator />
                                <CommandGroup heading="All timezones">
                                    {otherTimezones.map((tz) => (
                                        <TimezoneOption
                                            key={tz}
                                            timezone={tz}
                                            selected={selected}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                {canSave ? (
                    <ConfirmDialog
                        title="Update Timezone"
                        description="Save changes to the organisation timezone?"
                        cancel="Cancel"
                        action="Update"
                        handleAction={handleConfirm}
                    >
                        <Button className="mt-6 yvr-button">
                            Save Changes
                        </Button>
                    </ConfirmDialog>
                ) : (
                    <Button disabled className="mt-6 yvr-button">
                        Save Changes
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}
